import { __extends } from "tslib";
import { ExternalConfigCache, InMemoryConfigCache } from "./ConfigCatCache";
import { ConfigCatConsoleLogger, LoggerWrapper } from "./ConfigCatLogger";
import { DefaultEventEmitter } from "./DefaultEventEmitter";
import { Hooks } from "./Hooks";
import { ProjectConfig } from "./ProjectConfig";
import { sha1 } from "./Sha1";
/** Specifies the supported polling modes. */
export var PollingMode;
(function (PollingMode) {
    /** The ConfigCat SDK downloads the latest values automatically and stores them in the local cache. */
    PollingMode[PollingMode["AutoPoll"] = 0] = "AutoPoll";
    /** The ConfigCat SDK downloads the latest setting values only if they are not present in the local cache, or if the cache entry has expired. */
    PollingMode[PollingMode["LazyLoad"] = 1] = "LazyLoad";
    /** The ConfigCat SDK will not download the config JSON automatically. You need to update the cache manually, by calling `forceRefresh()`. */
    PollingMode[PollingMode["ManualPoll"] = 2] = "ManualPoll";
})(PollingMode || (PollingMode = {}));
/** Controls the location of the config JSON files containing your feature flags and settings within the ConfigCat CDN. */
export var DataGovernance;
(function (DataGovernance) {
    /** Choose this option if your config JSON files are published to all global CDN nodes. */
    DataGovernance[DataGovernance["Global"] = 0] = "Global";
    /** Choose this option if your config JSON files are published to CDN nodes only in the EU. */
    DataGovernance[DataGovernance["EuOnly"] = 1] = "EuOnly";
})(DataGovernance || (DataGovernance = {}));
/* eslint-disable @typescript-eslint/no-inferrable-types */
var OptionsBase = /** @class */ (function () {
    function OptionsBase(apiKey, clientVersion, options, defaultCacheFactory, eventEmitterFactory) {
        var _this = this;
        var _a, _b, _c;
        this.requestTimeoutMs = 30000;
        this.baseUrlOverriden = false;
        this.proxy = "";
        this.offline = false;
        if (!apiKey) {
            throw new Error("Invalid 'apiKey' value");
        }
        this.apiKey = apiKey;
        this.clientVersion = clientVersion;
        this.dataGovernance = (_a = options === null || options === void 0 ? void 0 : options.dataGovernance) !== null && _a !== void 0 ? _a : DataGovernance.Global;
        switch (this.dataGovernance) {
            case DataGovernance.EuOnly:
                this.baseUrl = "https://cdn-eu.configcat.com";
                break;
            default:
                this.baseUrl = "https://cdn-global.configcat.com";
                break;
        }
        var eventEmitter = (_b = eventEmitterFactory === null || eventEmitterFactory === void 0 ? void 0 : eventEmitterFactory()) !== null && _b !== void 0 ? _b : new DefaultEventEmitter();
        this.hooks = new Hooks(eventEmitter);
        this.readyPromise = new Promise(function (resolve) { return _this.hooks.once("clientReady", resolve); });
        var logger;
        var cache;
        if (options) {
            logger = options.logger;
            cache = options.cache;
            if (options.requestTimeoutMs) {
                if (options.requestTimeoutMs < 0) {
                    throw new Error("Invalid 'requestTimeoutMs' value");
                }
                this.requestTimeoutMs = options.requestTimeoutMs;
            }
            if (options.baseUrl) {
                this.baseUrl = options.baseUrl;
                this.baseUrlOverriden = true;
            }
            if (options.proxy) {
                this.proxy = options.proxy;
            }
            if (options.flagOverrides) {
                this.flagOverrides = options.flagOverrides;
            }
            if (options.defaultUser) {
                this.defaultUser = options.defaultUser;
            }
            if (options.offline) {
                this.offline = options.offline;
            }
            (_c = options.setupHooks) === null || _c === void 0 ? void 0 : _c.call(options, this.hooks);
        }
        this.logger = new LoggerWrapper(logger !== null && logger !== void 0 ? logger : new ConfigCatConsoleLogger(), this.hooks);
        this.cache = cache
            ? new ExternalConfigCache(cache, this.logger)
            : (defaultCacheFactory ? defaultCacheFactory(this) : new InMemoryConfigCache());
    }
    OptionsBase.prototype.getUrl = function () {
        return this.baseUrl + "/configuration-files/" + this.apiKey + "/" + OptionsBase.configFileName + "?sdk=" + this.clientVersion;
    };
    OptionsBase.prototype.getCacheKey = function () {
        return sha1(this.apiKey + "_" + OptionsBase.configFileName + "_" + ProjectConfig.serializationFormatVersion);
    };
    OptionsBase.configFileName = "config_v5.json";
    return OptionsBase;
}());
export { OptionsBase };
var AutoPollOptions = /** @class */ (function (_super) {
    __extends(AutoPollOptions, _super);
    function AutoPollOptions(apiKey, sdkType, sdkVersion, options, defaultCacheFactory, eventEmitterFactory) {
        var _this = _super.call(this, apiKey, sdkType + "/a-" + sdkVersion, options, defaultCacheFactory, eventEmitterFactory) || this;
        _this.pollIntervalSeconds = 60;
        _this.maxInitWaitTimeSeconds = 5;
        if (options) {
            if (options.pollIntervalSeconds !== void 0 && options.pollIntervalSeconds !== null) {
                _this.pollIntervalSeconds = options.pollIntervalSeconds;
            }
            if (options.maxInitWaitTimeSeconds !== void 0 && options.maxInitWaitTimeSeconds !== null) {
                _this.maxInitWaitTimeSeconds = options.maxInitWaitTimeSeconds;
            }
        }
        // https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#maximum_delay_value
        // https://stackoverflow.com/a/3468650/8656352
        var maxSetTimeoutIntervalSecs = 2147483;
        if (!(typeof _this.pollIntervalSeconds === "number" && 1 <= _this.pollIntervalSeconds && _this.pollIntervalSeconds <= maxSetTimeoutIntervalSecs)) {
            throw new Error("Invalid 'pollIntervalSeconds' value");
        }
        if (!(typeof _this.maxInitWaitTimeSeconds === "number" && _this.maxInitWaitTimeSeconds <= maxSetTimeoutIntervalSecs)) {
            throw new Error("Invalid 'maxInitWaitTimeSeconds' value");
        }
        return _this;
    }
    return AutoPollOptions;
}(OptionsBase));
export { AutoPollOptions };
var ManualPollOptions = /** @class */ (function (_super) {
    __extends(ManualPollOptions, _super);
    function ManualPollOptions(apiKey, sdkType, sdkVersion, options, defaultCacheFactory, eventEmitterFactory) {
        return _super.call(this, apiKey, sdkType + "/m-" + sdkVersion, options, defaultCacheFactory, eventEmitterFactory) || this;
    }
    return ManualPollOptions;
}(OptionsBase));
export { ManualPollOptions };
var LazyLoadOptions = /** @class */ (function (_super) {
    __extends(LazyLoadOptions, _super);
    function LazyLoadOptions(apiKey, sdkType, sdkVersion, options, defaultCacheFactory, eventEmitterFactory) {
        var _this = _super.call(this, apiKey, sdkType + "/l-" + sdkVersion, options, defaultCacheFactory, eventEmitterFactory) || this;
        _this.cacheTimeToLiveSeconds = 60;
        if (options) {
            if (options.cacheTimeToLiveSeconds !== void 0 && options.cacheTimeToLiveSeconds !== null) {
                _this.cacheTimeToLiveSeconds = options.cacheTimeToLiveSeconds;
            }
        }
        if (!(typeof _this.cacheTimeToLiveSeconds === "number" && 1 <= _this.cacheTimeToLiveSeconds && _this.cacheTimeToLiveSeconds <= 2147483647)) {
            throw new Error("Invalid 'cacheTimeToLiveSeconds' value");
        }
        return _this;
    }
    return LazyLoadOptions;
}(OptionsBase));
export { LazyLoadOptions };
